import { useState, useEffect, useCallback } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Select from '@cloudscape-design/components/select';
import Multiselect from '@cloudscape-design/components/multiselect';
import DatePicker from '@cloudscape-design/components/date-picker';
import Input from '@cloudscape-design/components/input';
import Toggle from '@cloudscape-design/components/toggle';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Alert from '@cloudscape-design/components/alert';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { getFilters, getQueueStatus, scopedResummarize, triggerScopedSync, getLastOperation, LastOperation } from '../../api/tickets';
import { toGroupOptions, toSeverityOptions } from '../experiments/experimentShared';
import { lastOperationText } from './operationFormat';

type Opt = { label: string; value: string };

const STATUS_OPTIONS: Opt[] = [
  'Pending',
  'Assigned',
  'Work in Progress',
  'Researching',
  'Resolved',
  'Closed',
].map((s) => ({ label: s, value: s }));
const OPERATIONS: Opt[] = [
  { label: 'Re-summarize (current AI model/prompt, no re-fetch)', value: 'resummarize' },
  { label: 'Partial sync (re-fetch from Tickety)', value: 'sync' },
];

export function ScopedOperations() {
  const [operation, setOperation] = useState<Opt>(OPERATIONS[0]);
  const [timeBasis, setTimeBasis] = useState<'created' | 'updated'>('created');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [groupOptions, setGroupOptions] = useState<Opt[]>([]);
  const [groups, setGroups] = useState<Opt[]>([]);
  const [severities, setSeverities] = useState<Opt[]>([]);
  const [severityOptions, setSeverityOptions] = useState<Opt[]>([]);
  const [statuses, setStatuses] = useState<Opt[]>([]);
  const [tags, setTags] = useState('');

  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [lastOp, setLastOp] = useState<LastOperation | null>(null);
  const [inFlight, setInFlight] = useState(0);
  const lastOpText = lastOperationText(lastOp);

  const loadLastOp = useCallback(() => {
    getLastOperation()
      .then(({ lastOperation }) => setLastOp(lastOperation))
      .catch(() => setLastOp(null));
  }, []);

  /** Poll the queue while anything is in flight. Without this the panel showed a
   * submitted operation and never changed, which is why a run looked finished
   * the moment it started. */
  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      try {
        const qs = await getQueueStatus();
        if (!active) return;
        const depth = qs.queue.messagesAvailable + qs.queue.messagesInFlight;
        setInFlight(depth);
        timer = setTimeout(tick, depth > 0 ? 5000 : 20000);
      } catch {
        if (active) timer = setTimeout(tick, 30000);
      }
    };
    tick();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    getFilters()
      .then(({ filters }) => {
        setGroupOptions(toGroupOptions(filters));
        setSeverityOptions(toSeverityOptions(filters));
      })
      .catch(() => {
        setGroupOptions([]);
        setSeverityOptions(toSeverityOptions(undefined));
      });
    loadLastOp();
  }, [loadLastOp]);

  const handleRun = useCallback(async () => {
    setMessage(null);
    if (dateFrom && dateTo && dateFrom > dateTo) {
      setMessage({ type: 'error', text: 'Start date must be on or before end date.' });
      return;
    }
    setRunning(true);
    const scope = {
      timeBasis,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      assignedGroups: groups.length ? groups.map((g) => g.value) : undefined,
      severities: severities.length ? severities.map((s) => s.value) : undefined,
      statuses: statuses.length ? statuses.map((s) => s.value) : undefined,
      tags: tags.trim() ? tags.split(',').map((t) => t.trim()).filter(Boolean) : undefined,
    };
    try {
      if (operation.value === 'resummarize') {
        const { count } = await scopedResummarize(scope);
        setMessage({ type: 'success', text: `Queued ${count} ticket(s) for re-summarization with the current AI config.` });
      } else {
        await triggerScopedSync(scope);
        setMessage({ type: 'success', text: 'Partial sync started for the selected scope.' });
      }
      setTimeout(loadLastOp, 1500);
      getQueueStatus()
        .then((qs) => setInFlight(qs.queue.messagesAvailable + qs.queue.messagesInFlight))
        .catch(() => undefined);
    } catch (e: any) {
      setMessage({ type: 'error', text: `Failed: ${e.message}` });
    } finally {
      setRunning(false);
    }
  }, [operation, timeBasis, dateFrom, dateTo, groups, severities, statuses, tags, loadLastOp]);

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Re-summarize or re-fetch a targeted subset of tickets — e.g. re-run a newer AI model/prompt over a slice without reprocessing everything."
        >
          Targeted re-summarize / partial sync
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* Execution state. The queue is the only live signal that work is in
            flight, so it belongs beside the button that starts it — and it is
            shared with scheduled ingestion, which the wording makes explicit
            rather than implying it counts only this run. */}
        {inFlight > 0 && (
          <StatusIndicator type="in-progress">
            {inFlight} ticket{inFlight > 1 ? 's' : ''} being processed now (all ingestion, not just
            this run)
          </StatusIndicator>
        )}
        {lastOpText ? (
          <StatusIndicator type={inFlight > 0 ? 'in-progress' : 'success'}>
            {lastOpText}
          </StatusIndicator>
        ) : (
          <Box variant="small" color="text-body-secondary">
            No targeted operation run yet.
          </Box>
        )}

        <ColumnLayout columns={2}>
          <FormField label="Operation">
            <Select
              selectedOption={operation}
              options={OPERATIONS}
              onChange={({ detail }) => setOperation(detail.selectedOption as Opt)}
              disabled={running}
            />
          </FormField>
          <FormField label="Time basis">
            <Toggle checked={timeBasis === 'updated'} onChange={({ detail }) => setTimeBasis(detail.checked ? 'updated' : 'created')} disabled={running}>
              {timeBasis === 'updated' ? 'Last-updated' : 'Created'}
            </Toggle>
          </FormField>
          <FormField label="From">
            <DatePicker value={dateFrom} onChange={({ detail }) => setDateFrom(detail.value)} placeholder="YYYY/MM/DD" disabled={running} />
          </FormField>
          <FormField label="To">
            <DatePicker value={dateTo} onChange={({ detail }) => setDateTo(detail.value)} placeholder="YYYY/MM/DD" disabled={running} />
          </FormField>
          <FormField label="Resolver groups">
            <Multiselect selectedOptions={groups} options={groupOptions} onChange={({ detail }) => setGroups(detail.selectedOptions as Opt[])} placeholder="All configured groups" disabled={running} />
          </FormField>
          <FormField label="Severities">
            <Multiselect selectedOptions={severities} options={severityOptions} onChange={({ detail }) => setSeverities(detail.selectedOptions as Opt[])} placeholder="Any severity" disabled={running} />
          </FormField>
          <FormField label="Statuses">
            <Multiselect selectedOptions={statuses} options={STATUS_OPTIONS} onChange={({ detail }) => setStatuses(detail.selectedOptions as Opt[])} placeholder="Any status" disabled={running} />
          </FormField>
          <FormField label="Tags (comma-separated)">
            <Input value={tags} onChange={({ detail }) => setTags(detail.value)} placeholder="tag1, tag2" disabled={running} />
          </FormField>
        </ColumnLayout>

        {operation.value === 'resummarize' && (
          <Box variant="small" color="text-body-secondary">
            Re-summarize reuses the current AI model/prompt (edit them in AI Summarization above) and regenerates from stored title + description only — it does not re-fetch comments from Tickety.
          </Box>
        )}

        {message && <Alert type={message.type}>{message.text}</Alert>}

        <Button variant="primary" onClick={handleRun} loading={running} disabled={running}>
          Run
        </Button>
      </SpaceBetween>
    </Container>
  );
}
